import { z } from 'zod'

/**
 * Tipos de cuenta disponibles
 */
export const TIPOS_CUENTA = ['Banco', 'Efectivo', 'Wallet', 'Otro'] as const
export type TipoCuenta = (typeof TIPOS_CUENTA)[number]

/**
 * Monedas disponibles
 */
export const MONEDAS = ['ARS', 'USD', 'EUR'] as const
export type Moneda = (typeof MONEDAS)[number]

/**
 * Labels legibles para las monedas
 */
export const MONEDA_LABELS: Record<Moneda, string> = {
  ARS: 'Peso Arg.',
  USD: 'Dólar',
  EUR: 'Euro',
}

/**
 * Opciones para selects de tipo de cuenta
 */
export const TIPOS_CUENTA_OPTIONS = TIPOS_CUENTA.map((tipo) => ({
  value: tipo,
  label: tipo,
}))

/**
 * Opciones para selects de moneda
 */
export const MONEDAS_OPTIONS = MONEDAS.map((moneda) => ({
  value: moneda,
  label: MONEDA_LABELS[moneda],
}))

/**
 * Validador de CBU (22 dígitos numéricos)
 */
const cbuRegex = /^\d{22}$/

/**
 * Schema Zod para formulario de Cuenta
 */
export const cuentaSchema = z.object({
  nombre: z
    .string()
    .min(1, 'El nombre es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres'),

  tipo: z.enum(TIPOS_CUENTA, {
    error: 'Selecciona un tipo de cuenta',
  }),

  institucion: z
    .string()
    .max(100, 'La institución no puede exceder 100 caracteres')
    .nullable()
    .optional()
    .transform((val) => val?.trim() || null),

  cbu_cvu_alias: z
    .string()
    .nullable()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true
        const cleaned = val.replace(/\s/g, '')
        // Es un CBU (22 dígitos) o un alias (hasta 20 caracteres alfanuméricos con puntos)
        return cbuRegex.test(cleaned) || (cleaned.length <= 20 && /^[a-zA-Z0-9.]+$/.test(cleaned))
      },
      {
        message: 'Ingresa un CBU válido (22 dígitos) o un alias (hasta 20 caracteres)',
      }
    )
    .transform((val) => val?.trim() || null),

  moneda: z.enum(MONEDAS).default('ARS'),

  observaciones: z
    .string()
    .max(500, 'Las observaciones no pueden exceder 500 caracteres')
    .nullable()
    .optional()
    .transform((val) => val?.trim() || null),

  activa: z.boolean().default(true),

  saldo_inicial: z
    .number()
    .nullable()
    .optional()
    .default(0),
})

/**
 * Tipo inferido del schema
 */
export type CuentaFormValues = z.infer<typeof cuentaSchema>

/**
 * Valores por defecto para crear una cuenta nueva
 */
export const DEFAULT_CUENTA_VALUES: CuentaFormValues = {
  nombre: '',
  tipo: 'Banco',
  institucion: null,
  cbu_cvu_alias: null,
  moneda: 'ARS',
  observaciones: null,
  activa: true,
  saldo_inicial: 0,
}
